import { ArrowBack, ArrowForward, ArrowForward as ArrowNext } from '@mui/icons-material';
import { Box, Button, Card, CardContent, CardMedia, Container, Grid, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const DetailedProjects = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 3;

  const projects = [
    {
      id: 1,
      image: 'linear-gradient(135deg, #0f4c5c 0%, #1a5f7a 50%, #2a7f9e 100%)',
      logo: 'Cohere',
      date: t('detailedProjects.project1.date'),
      team: t('detailedProjects.project1.team'),
      title: t('detailedProjects.project1.title'),
      link: '#',
    },
    {
      id: 2,
      image: 'linear-gradient(135deg, #d4a574 0%, #c9a168 50%, #b88a4f 100%)',
      logo: 'PAR',
      date: t('detailedProjects.project2.date'),
      team: t('detailedProjects.project2.team'),
      title: t('detailedProjects.project2.title'),
      link: '#',
    },
    {
      id: 3,
      image: 'linear-gradient(135deg, #1a5f5f 0%, #8b2e2e 100%)',
      logo: 'Command A',
      logoSuffix: 'TRANSLATE',
      date: t('detailedProjects.project3.date'),
      team: t('detailedProjects.project3.team'),
      title: t('detailedProjects.project3.title'),
      link: '#',
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? '#0f172a'
            : '#f8f9fa',
      }}
    >
      <Container maxWidth="lg">
        {/* Project Cards Grid */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {projects.map((project) => (
            <Grid item xs={12} md={4} key={project.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '#1e293b'
                      : '#ffffff',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {/* Card Image/Banner */}
                <CardMedia
                  sx={{
                    height: 200,
                    background: project.image,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  {/* Logo */}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {/* Icon circles */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.3,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                          }}
                        />
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                          }}
                        />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: { xs: '1.5rem', md: '1.75rem' },
                        }}
                      >
                        {project.logo}
                      </Typography>
                    </Box>
                    {project.logoSuffix && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#fff',
                          fontSize: '0.625rem',
                          fontWeight: 600,
                          letterSpacing: '0.1em',
                          opacity: 0.9,
                        }}
                      >
                        {project.logoSuffix}
                      </Typography>
                    )}
                  </Box>
                </CardMedia>

                {/* Card Content */}
                <CardContent
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3,
                  }}
                >
                  {/* Date and Team */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.6)'
                          : 'rgba(0, 0, 0, 0.6)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      mb: 2,
                    }}
                  >
                    {project.team} - {project.date}
                  </Typography>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '1.125rem', md: '1.25rem' },
                      fontWeight: 500,
                      lineHeight: 1.4,
                      mb: 3,
                      flex: 1,
                      color: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '#fff'
                          : '#1e293b',
                    }}
                  >
                    {project.title}
                  </Typography>

                  {/* Read More Link */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pt: 2,
                      borderTop: (theme) =>
                        `1px solid ${theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.1)'
                        }`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '#fff'
                            : '#1e293b',
                      }}
                    >
                      {t('detailedProjects.readMore')}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '#fff'
                            : '#1e293b',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <ArrowForward />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            mt: 4,
          }}
        >
          <IconButton
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'rgba(0, 0, 0, 0.7)',
              '&:disabled': {
                color: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.3)',
              },
            }}
          >
            <ArrowBack />
          </IconButton>

          {[...Array(totalPages)].map((_, index) => (
            <Button
              key={index + 1}
              onClick={() => handlePageChange(index + 1)}
              sx={{
                minWidth: 40,
                height: 40,
                borderRadius: 1,
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: (theme) =>
                  currentPage === index + 1
                    ? '#fff'
                    : theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : 'rgba(0, 0, 0, 0.7)',
                background: (theme) =>
                  currentPage === index + 1
                    ? theme.palette.mode === 'dark'
                      ? '#1976d2'
                      : '#1976d2'
                    : 'transparent',
                '&:hover': {
                  background: (theme) =>
                    currentPage === index + 1
                      ? '#1565c0'
                      : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              {index + 1}
            </Button>
          ))}

          <IconButton
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'rgba(0, 0, 0, 0.7)',
              '&:disabled': {
                color: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.3)',
              },
            }}
          >
            <ArrowNext />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
};

export default DetailedProjects;

